<template>
  <div class="uploader">
    <draggable
      :list="newFileList"
      :options="{ animation: 200 }"
      item-key="url"
      class="upload-list picture-card"
      :disabled="!showUploadRemoveBtn || isFileUploading"
      @start="dragging = true"
      @end="dragging = false"
      @change="onChangeFileList"
    >
      <template #item="{ element }">
        <TransitionGroup tag="div">
          <div
            v-if="element.status === 'uploading'"
            :key="`uploading-${element.uid}`"
            class="upload-list-picture"
          >
            <div class="upload-progress">
              <a-progress
                type="circle"
                size="mini"
                :show-text="false"
                :status="element.status"
                :percent="element.percent"
              />
            </div>
            <div
              class="upload-icon upload-icon-remove upload-progress-remove-icon"
              @click="handleRemove(element)"
            >
              <icon-delete />
            </div>
          </div>
          <div v-else :key="`done-${element.uid}`" class="upload-list-picture">
            <img
              v-if="!element.fileIcon || element.fileIcon === 'image'"
              :src="dealWithPic(element.url, 'pic200wh')"
              :alt="element.name"
              :loading="imageLoading"
            />
            <div
              v-if="!(!element.fileIcon || element.fileIcon === 'image')"
              class="flex flex-col items-center p4"
            >
              <IconFile class="upload-list-picture-icon" />
              <span class="upload-list-picture-name overflow-ellipsis">{{
                element.name
              }}</span>
            </div>
            <div
              v-if="element.status === 'error'"
              class="upload-list-picture-error"
            >
              <div class="upload-list-picture-error-tip">
                <a-tooltip content="上传失败">
                  <span class="upload-icon-error">
                    <!-- <IconImageClose /> -->
                    <IconExclamationCircleFill />
                  </span>
                </a-tooltip>
              </div>
            </div>
            <div class="upload-list-picture-mask">
              <div class="upload-list-picture-operation">
                <div
                  v-if="!element.fileIcon || element.fileIcon === 'image'"
                  class="upload-icon upload-icon-preview"
                  @click="handlePreview(element)"
                >
                  <IconEye />
                </div>
                <div
                  v-if="element.fileIcon && element.fileIcon !== 'image'"
                  class="upload-icon upload-icon-dowload"
                  @click="(e) => download(e, element)"
                >
                  <IconDownload />
                </div>
                <div
                  v-if="['init', 'error'].includes(element.status)"
                  class="upload-icon upload-icon-preview"
                  @click="handleUpload(element)"
                >
                  <IconUpload />
                </div>
                <div
                  v-if="showUploadRemoveBtn"
                  class="upload-icon upload-icon-remove"
                  @click="handleRemove(element)"
                >
                  <icon-delete />
                </div>
              </div>
            </div>
          </div>
        </TransitionGroup>
      </template>
      <template #footer>
        <a-upload
          ref="arcoUploadRef"
          :file-list="fileList"
          :list-type="listType"
          image-preview
          multiple
          download
          :accept="accept"
          :custom-request="customRequest"
          :on-before-remove="onBeforeRemove"
          :show-retry-button="false"
          :show-upload-button="showUploadRemoveBtn && !isExceedLimit"
          :show-remove-button="showUploadRemoveBtn"
          :custom-icon="getCustomIcon()"
          v-bind="$attrs"
          :limit="limit"
          :on-before-upload="onBeforeUpload"
          @exceed-limit="exceedLimit"
        >
        </a-upload>
      </template>
    </draggable>
    <ImagePreviewGroup
      :src-list="previewList"
      :visible="imagePreviewVisible"
      :current="imagePreviewCurrent"
      @change="handlePreviewChange"
      @visible-change="handlePreviewVisibleChange"
    />
  </div>
</template>

<script lang="ts" setup>
  import {
    FileItem,
    Message,
    RequestOption,
    CustomIcon,
    ImagePreviewGroup,
  } from '@arco-design/web-vue';
  import {
    getSignedurl,
    saveFileBySingedUrl,
    saveFileInfo,
  } from '@/api/uploader';
  import {
    computed,
    nextTick,
    ref,
    unref,
    h,
    resolveComponent,
    watch,
    toRefs,
  } from 'vue';
  import {
    getFileType,
    getFileSuffix,
    getTypeBySuffix,
    dealWithPic,
  } from '@/utils/common';
  import draggable from 'vuedraggable';
  import axios from 'axios';
  import { v4 as uuidv4 } from 'uuid';
  import { basicProps, STATUS } from './props';

  interface FileItemExtra extends FileItem {
    cancel?: () => void;
  }

  const { CancelToken, isCancel } = axios;
  const props = defineProps(basicProps);
  const emits = defineEmits([
    'update:file-list',
    'preview',
    'change',
    'exceedLimit',
  ]);
  const dragging = ref(false);
  const { fileList } = toRefs(props);
  const newFileList: any = ref([]);
  const isExceedLimit = computed(() => {
    return fileList.value.length >= props.limit;
  });
  // 如果上传中调整文件顺序会导致有些图片显示不了的问题
  const isFileUploading = computed(() => {
    const findItem = newFileList.value.find(
      (item: any) => item.status === 'uploading'
    );
    return !!findItem;
  });
  const renderFileIcon = (file: Recordable) => {
    if (!file.name) {
      file.name = file.fileName;
    }
    let type = 'file';
    if (file.file && file.file.type) {
      type = file.file.type;
    } else {
      let extension = file.name?.split('.')[1] ?? '';
      if (!extension && file.url) {
        const arr = file.url?.split('.');
        extension = arr[arr.length - 1];
      }
      extension = extension?.toLowerCase();
      if (['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp'].includes(extension)) {
        type = 'image';
      } else if (['mp4', 'm2v', 'mkv', 'm4v', 'mov'].includes(extension)) {
        type = 'video';
      } else if (
        ['mp3', 'wav', 'wmv', 'm4a', 'acc', 'flac'].includes(extension)
      ) {
        type = 'audio';
      }
    }

    return type;
  };
  watch(
    fileList,
    (fileList) => {
      fileList.forEach((file: Recordable) => {
        file.fileIcon = renderFileIcon(file);
        file.uid = file.uid || uuidv4();
      });
      newFileList.value = fileList;
    },
    { immediate: true, deep: true }
  );
  const previewList = computed(() => {
    return fileList.value.map((file: Recordable) => file.url);
  });
  const exceedLimit = () => {
    Message.warning('上传文件数量超出限制');
  };
  const onChangeFileList = (data: any) => {
    fileList.value.forEach((file, idx) => {
      file.showOrder = idx;
    });
  };
  const imagePreviewVisible = ref(false);
  const imagePreviewCurrent = ref(0);
  const handlePreviewVisibleChange = (visible: boolean) => {
    imagePreviewVisible.value = visible;
  };
  const handlePreviewChange = (current: number) => {
    imagePreviewCurrent.value = current;
  };
  const handleUpload = (fileItem: FileItemExtra) => {
    if (
      arcoUploadRef.value?.submit &&
      typeof arcoUploadRef.value.submit === 'function'
    ) {
      handleRemove(fileItem);
      arcoUploadRef.value?.submit(fileItem);
    }
  };
  const handlePreview = (fileItem: FileItemExtra) => {
    if (fileItem.url) {
      const current = previewList.value.indexOf(fileItem.url);
      if (current > -1) {
        imagePreviewCurrent.value = current;
        imagePreviewVisible.value = true;
      }
    }

    emits('preview', fileItem);
  };
  // 删除
  const handleRemove = (fileItem: FileItemExtra) => {
    const tempList = newFileList.value.filter((item: Recordable) => {
      return item.url !== fileItem.url;
    });
    const idx = fileList.value.findIndex(
      (file: Recordable) => file.url === fileItem.url
    );
    if (idx > -1) {
      fileList.value.splice(idx, 1);
    }
    newFileList.value = tempList;
    emits('update:file-list', fileList.value);
    if (fileItem.cancel && typeof fileItem.cancel === 'function') {
      fileItem.cancel();
    }
  };

  /**
   * @param {Array} fileList 文件数组
   * @param {number} index 当问文件 index
   * @param {string} status 状态 uploading: 上传中 failed：上传失败 done：上传完成
   * @param {string} customText 自定义的上传状态文字
   */
  const changeFileStatus = (
    status: string,
    fileList: Array<any> = [],
    file: any = {}
  ) => {
    file.status = STATUS[status];
    fileList.push(file);
    return fileList;
  };
  const download = (e: any, file: any) => {
    const element = e.target.parentNode;
    // eslint-disable-next-line no-script-url
    element.setAttribute('href', `javascript:void(0);`);
    element.setAttribute('target', `_self`);

    const x = new window.XMLHttpRequest();
    x.open('GET', file.url, true);
    x.responseType = 'blob';
    x.onload = () => {
      const url = window.URL.createObjectURL(x.response);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName || file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };
    x.send();
  };
  const getCustomIcon = () => {
    return {
      fileName: (file: any) => {
        return h(
          'div',
          {
            onClick: (e: Event) => download(e, file),
          },
          file.name || file.fileName
        );
      },
      fileIcon: (file: any) => {
        const extension =
          file.name?.split('.')[1] ?? file.fileName?.split('.')[1] ?? '';
        const isImageType = [
          'png',
          'jpg',
          'jpeg',
          'bmp',
          'gif',
          'webp',
        ].includes(extension);
        const ListImgPopover = resolveComponent('ListImgPopover');
        if (file.fileType && isImageType) {
          return h(
            ListImgPopover,
            { imgUrl: file.url, width: 20, height: 20 },
            {}
          );
        }
        return null;
      },
    } as CustomIcon;
  };

  /**
   * 用于在上传过程中更新附件字段
   * @param uid 附件标识
   * @param updateField 更新附件的字段
   */
  const updateFile = (uid: number, updateField: Record<string, any> = {}) => {
    const currentFile = props.fileList.find((item) => item.uid === uid);
    if (currentFile) {
      Object.assign(currentFile, updateField);
    }
  };

  // 截取字符串的后几位
  const cutString = (name: string, num: number): string => {
    try {
      if (name.length <= num) {
        return name;
      }
      const suffix = getFileSuffix(name);
      // 先去除后缀，再截取，补充后缀
      return `${name
        .substring(0, name.length - suffix.length - 1)
        .substring(0, num - suffix.length - 1)}.${suffix}`;
    } catch (error) {
      return name;
    }
  };
  // 在文件名称后加时间戳
  const getTimeStampFileName = (name: string): string => {
    const suffix = name?.split('.')?.pop() ?? '';
    return `${
      name.substring(0, name.length - suffix.length - 1) + new Date().getTime()
    }.${suffix}`;
  };
  const arcoUploadRef = ref();
  const onBeforeRemove = (file: FileItemExtra): Promise<boolean> => {
    const { url } = file;

    const sourceFileList = unref((arcoUploadRef.value as any).fileList);
    const index = sourceFileList.findIndex((item: any) => item.url === url);
    if (index > -1) {
      sourceFileList.splice(index, 1);
      emits('update:file-list', sourceFileList);
      return Promise.resolve(true);
    }
    return Promise.reject();
  };
  // 上传前校验
  const onBeforeUpload = (file: File) => {
    const { size, name, type } = file;
    const { maxSize, accept } = props;
    if (maxSize && file.size >= maxSize) {
      Message.error(
        `文件超过大小限制。文件大小不可超过${maxSize / 1024 / 1024}MB`
      );
      return false;
    }
    if (accept === 'image/*' && !/^image/.test(type)) {
      Message.error(`只能上传图片`);
      return false;
    }
    return true;
  };
  // 保存文件信息
  const saveFileInfoFn = (signedUrlDTO: any, uid: number) => {
    saveFileInfo(
      signedUrlDTO,
      new CancelToken((c) => {
        updateFile(uid, { cancel: c });
      })
    )
      .then((res: any) => {
        // 加上图片ID
        if (res.data?.id) {
          updateFile(uid, {
            fileId: res.data.id,
            status: STATUS.success,
            percent: 1,
            cancel: null,
            file: null,
          });
        }
      })
      .catch((error: Error) => {
        if (isCancel(error)) {
          console.log('Request canceled', error.message);
        } else {
          updateFile(uid, {
            status: STATUS.fail,
          });
        }
      });
  };
  const saveFileBySingedUrlFn = (
    signedUrlInfo: any,
    readerResult: any,
    uid: number
  ) => {
    updateFile(uid, {
      percent: 0.5,
    });
    saveFileBySingedUrl(
      signedUrlInfo.signedUrl,
      readerResult,
      new CancelToken((c) => {
        updateFile(uid, { cancel: c });
      })
    )
      .then((res) => {
        if (res.status === 200) {
          Message.success({
            content: '上传成功！',
            duration: 2000,
          });
          updateFile(uid, {
            percent: 0.75,
          });
          nextTick(() => {
            updateFile(uid, {
              percent: 0.85,
            });
            saveFileInfoFn(signedUrlInfo, uid);
          });
        }
      })
      .catch((error) => {
        if (isCancel(error)) {
          console.log('Request canceled', error.message);
        } else {
          updateFile(uid, {
            status: STATUS.fail,
          });
        }
      });
    return null;
  };
  const customRequest: any = async (options: RequestOption): Promise<any> => {
    const { onProgress, fileItem } = options;
    const { file, uid = uuidv4(), percent } = fileItem as any;
    if (props.limit > 0 && fileList.value.length + 1 > props.limit) {
      emits('exceedLimit', fileList.value, fileItem);
      return;
    }
    const loadFileBob = (file: File) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        let blob = null;
        reader.onload = (e: any) => {
          if (typeof e.target.result === 'object') {
            blob = new Blob([e.target.result]);
          } else {
            blob = e.target.result;
          }
          resolve(blob);
        };
        reader.onloadend = () => {
          if (reader.error) {
            Message.error('获取文件出错！');
          }
        };
      });
    };
    const blob = await loadFileBob(file);

    const typeText = file.type?.split && file.type?.split('/')[0];
    const fileType = getFileType(typeText);
    const fileSuffix = getFileSuffix(file.name);
    const fileName = cutString(file.name, 32);
    // const restLen = 32 - fileSuffix.length - 1;
    // const fileName = `${file.name.slice(0, restLen)}.${fileSuffix}`;
    const index = props.fileList.length;
    const fileIcon = renderFileIcon(file);
    emits(
      'update:file-list',
      changeFileStatus('uploading', props.fileList, {
        name: fileName,
        fileName,
        status: STATUS.init,
        index,
        uid,
        percent,
        fileIcon,
        file,
      })
    );
    onProgress(0.15);
    const data: Record<string, any> = {
      fileType,
      fileSuffix,
      moduleFlag: props.moduleFlag,
    };
    if (!['image', 'video'].includes(getTypeBySuffix(fileSuffix))) {
      // 附件将文件名传给后端
      data.fileName = getTimeStampFileName(file.name);
    }
    getSignedurl(
      data,
      new CancelToken((c) => {
        updateFile(uid, { cancel: c });
      })
    )
      .then((res: any) => {
        if (res.status === 200 && res.data) {
          updateFile(uid, {
            fileType: res.data.fileType,
            moduleFlag: res.data.moduleFlag,
            url: res.data.url,
            storeName: res.data.storeName,
            status: STATUS.uploading,
            type: fileSuffix,
            percent: 0.35,
            cancel: null,
          });
          saveFileBySingedUrlFn(res.data, blob, uid);
        }
      })
      .catch((error: Error) => {
        if (isCancel(error)) {
          console.log('Request canceled', error.message);
        } else {
          updateFile(uid, {
            status: STATUS.fail,
          });
        }
      });
  };
</script>

<script lang="ts">
  export default {
    name: 'QsUploader',
  };
</script>

<style lang="less" scoped>
  :deep(.arco-upload-list-item-thumbnail img) {
    max-width: 100%;
    max-height: 100%;
  }

  :deep(.arco-upload-list-picture),
  :deep(.arco-upload-progress),
  :deep(.arco-upload-list-item) {
    display: none;
  }

  :deep(.arco-upload-wrapper) {
    align-self: flex-end;
    // margin-left: 5px;
  }

  :deep(.arco-upload-picture-card) {
    box-sizing: border-box;
  }

  .image-item {
    cursor: pointer;
  }

  .uploader {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-start;
    // width: 100%;
    :deep(.arco-upload-wrapper) {
      width: inherit;
    }
  }

  .upload-list {
    display: flex;
    flex-wrap: wrap;
    // width: 100%;
    margin: 0;
    padding: 0;
    vertical-align: top;
    list-style: none;

    .list-item:first-of-type {
      margin-top: 24px;
    }

    &-item {
      position: relative;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      margin-top: 5px;
    }
  }

  .upload-list-picture {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 80px;
    height: 80px;
    margin-bottom: 0;
    overflow: hidden;
    color: var(--color-text-2);
    text-align: center;
    vertical-align: top;
    background: var(--color-fill-2);
    border: 1px dashed var(--color-neutral-3);
    border-radius: var(--border-radius-small);
    transition: all 0.1s cubic-bezier(0, 0, 1, 1);

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    &-icon {
      margin-top: 8px;
    }

    &-name {
      font-size: 12px;
    }

    .overflow-ellipsis {
      display: -webkit-box;
      margin: 5px;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-all;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
    }

    &:hover {
      color: var(--color-text-2);
      background-color: var(--color-fill-3);
      border-color: var(--color-neutral-4);
    }

    &-mask {
      position: absolute;
      color: var(--color-white);
      font-size: 16px;
      line-height: 80px;
      text-align: center;
      background: rgb(0 0 0 / 50%);
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.1s cubic-bezier(0, 0, 1, 1);
      inset: 0;

      &:hover {
        opacity: 1;
      }
    }

    &-error {
      position: absolute;
      color: var(--color-white);
      font-size: 16px;
      line-height: 80px;
      text-align: center;
      background: rgb(0 0 0 / 50%);
      cursor: pointer;
      opacity: 1;
      transition: opacity 0.1s cubic-bezier(0, 0, 1, 1);
      inset: 0;
    }

    &-operation {
      display: flex;
      justify-content: space-evenly;
      font-size: 14px;

      .upload-icon-remove {
        font-size: inherit;
      }
    }

    &-error-tip {
      color: rgb(var(--danger-6)) !important;
      font-size: 12px;

      .upload-icon-error {
        font-size: 26px;
      }
    }

    .upload-icon {
      width: 14px;
      height: 14px;
      cursor: pointer;

      &-remove {
        position: relative;
        font-size: 14px;
      }

      &-upload {
        color: rgb(var(--color-primary-6));
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:active,
        &:hover {
          color: rgb(var(--color-primary-7));
        }
      }
    }
  }

  .upload-progress {
    position: relative;
    display: inline-block;
    line-height: 1;

    &:hover {
      .upload-progress-circle-bg {
        stroke: rgb(var(--gray-10) 0.2);
      }

      .upload-progress-circle-bar {
        stroke: rgb(var(--primary-7));
      }
    }
  }

  .upload-progress-remove-icon {
    position: absolute !important;
    top: 2px;
    right: 2px;
  }
</style>
